import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, interval, switchMap, catchError, of } from 'rxjs';
import { Api } from './api';
import { AuthService } from './auth.service';

export interface ApiNotification {
  notificacion_id: string;
  titulo: string;
  descripcion: string;
  tipo_notificacion: string;
  usuario_id: string;
  tipo_entidad: string;
  entidad_id: string;
  accion_url: string;
  leida: boolean;
  leida_en: string | null;
  es_recurrente: boolean;
  intervalo_recurrencia: string | null;
  proxima_ejecucion: string | null;
  auto_marcar_leida_minutos: number | null;
  tipo_informacion: string;
  periodo_referencia: string;
  fecha_creacion: string;
  fecha_actualizacion: string;
}

export interface NotificationResponse {
  message: string;
  notificaciones: ApiNotification[];
  total: number;
}

export interface ProcessedNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  date: string;
  timeAgo: string;
  isRead: boolean;
  entityType: string;
  entityId: string;
  actionUrl: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  private notificationsSubject = new BehaviorSubject<ProcessedNotification[]>([]);
  private unreadCountSubject = new BehaviorSubject<number>(0);
  private isPollingSubject = new BehaviorSubject<boolean>(false);
  
  public notifications$ = this.notificationsSubject.asObservable();
  public unreadCount$ = this.unreadCountSubject.asObservable();
  public isPolling$ = this.isPollingSubject.asObservable();

  private pollingInterval = 30000; // 30 segundos
  private pollingSubscription: any;

  constructor(
    private api: Api,
    private authService: AuthService
  ) {}

  startPolling(): void {
    if (this.pollingSubscription) {
      return; // Ya está ejecutándose
    }

    if (!this.authService.isAuthenticated()) {
      console.warn('NotificationsService: Usuario no está logueado, no se puede iniciar polling');
      return;
    }

    this.isPollingSubject.next(true);
    console.log('NotificationsService: Iniciando polling de notificaciones');
    
    this.fetchNotifications();

    this.pollingSubscription = interval(this.pollingInterval)
      .pipe(
        switchMap(() => {
          if (!this.authService.isAuthenticated()) {
            console.warn('⚠️ NotificationsService: Usuario ya no está logueado, deteniendo polling');
            this.stopPolling();
            return of({ notificaciones: [], total: 0 });
          }
          return this.api.getMyNotifications();
        }),
        catchError(error => {
          console.error('❌ NotificationsService: Error fetching notifications:', error);
          return of({ notificaciones: [], total: 0 });
        })
      )
      .subscribe((response: any) => {
        this.processNotifications(response);
      });
  }

  stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
      this.isPollingSubject.next(false);
    }
  }

  fetchNotifications(): void {
    if (!this.authService.isAuthenticated()) {
      console.warn('NotificationsService: Usuario no está logueado, no se pueden obtener notificaciones');
      return;
    }

    this.api.getMyNotifications()
      .pipe(
        catchError(error => {
          console.error('NotificationsService: Error fetching notifications:', error);
          return of({ notificaciones: [], total: 0 });
        })
      )
      .subscribe((response: any) => {
        this.processNotifications(response);
      });
  }

  private processNotifications(response: NotificationResponse): void {
    const processedNotifications: ProcessedNotification[] = response.notificaciones.map(notification => ({
      id: notification.notificacion_id,
      type: notification.tipo_informacion || notification.tipo_notificacion,
      title: notification.titulo,
      message: notification.descripcion,
      date: notification.fecha_creacion,
      timeAgo: this.getTimeAgo(notification.fecha_creacion),
      isRead: notification.leida,
      entityType: notification.tipo_entidad,
      entityId: notification.entidad_id,
      actionUrl: notification.accion_url
    }));

    this.notificationsSubject.next(processedNotifications);
    this.updateUnreadCount(processedNotifications);
  }

  private updateUnreadCount(notifications: ProcessedNotification[]): void {
    const unreadCount = notifications.filter(n => !n.isRead).length;
    this.unreadCountSubject.next(unreadCount);
  }

  private getTimeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) {
      return 'Ahora';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minuto${diffInMinutes > 1 ? 's' : ''}`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hora${diffInHours > 1 ? 's' : ''}`;
    } else if (diffInDays < 7) {
      return `${diffInDays} día${diffInDays > 1 ? 's' : ''}`;
    } else {
      return date.toLocaleDateString('es-ES');
    }
  }

  getNotificationIcon(type: string): string {
    const icons = {
      'meta_incompleta': 'assets/svg/notif-warning.svg',
      'meta_proxima': 'assets/svg/notif-timeout.svg',
      'meta_proximo_vencimiento': 'assets/svg/notif-timeout.svg',
      'meta_completada': 'assets/svg/notif-success.svg',
      'baja_actividad': 'assets/svg/notif-caution.svg',
      'vencimiento': 'assets/svg/notif-timeout.svg'
    };
    return icons[type as keyof typeof icons] || 'assets/svg/notif-info.svg';
  }

  getNotificationClass(type: string): string {
    const classes = {
      'meta_incompleta': 'notification-error',
      'meta_proxima': 'notification-warning',
      'meta_proximo_vencimiento': 'notification-warning',
      'meta_completada': 'notification-success',
      'baja_actividad': 'notification-warning',
      'vencimiento': 'notification-warning'
    };
    return classes[type as keyof typeof classes] || 'notification-info';
  }

  markAsRead(notificationId: string): void {
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = currentNotifications.map(notification => {
      if (notification.id === notificationId) {
        return { ...notification, isRead: true };
      }
      return notification;
    });
    
    this.notificationsSubject.next(updatedNotifications);
    this.updateUnreadCount(updatedNotifications);
    
    this.api.markNotificationAsRead(notificationId).subscribe({
      error: (err) => console.error('Error marcando notificación como leída', err),
    });
  }

  markAllAsRead(): void {
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = currentNotifications.map(notification => ({
      ...notification,
      isRead: true
    }));
    
    this.notificationsSubject.next(updatedNotifications);
    this.updateUnreadCount(updatedNotifications);
    
    const ids = updatedNotifications.map(n => n.id);
      ids.forEach(id => {
        this.api.markNotificationAsRead(id).subscribe();
      });


  }

  deleteNotification(notificationId: string): void {
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = currentNotifications.filter(n => n.id !== notificationId);
    
    this.notificationsSubject.next(updatedNotifications);
    this.updateUnreadCount(updatedNotifications);
    
    // Aquí podrías hacer una llamada al API para eliminar la notificación
    // this.api.deleteNotification(notificationId).subscribe();
  }
}